import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import SiteNav from '../layout/SiteNav';

const Section = styled.section`
  position: relative;
  min-height: max(620px, 75vh);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.gradients.dark};

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(10px);
    pointer-events: none;
  }

  &::before {
    width: min(700px, 80vw);
    height: min(700px, 80vw);
    top: -40%;
    left: -15%;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.22), transparent 68%);
  }

  &::after {
    width: 520px;
    height: 520px;
    right: -12%;
    bottom: -46%;
    background: radial-gradient(circle, rgba(167, 139, 250, 0.18), transparent 68%);
  }
`;

const Content = styled(motion.div)`
  position: relative;
  z-index: 1;
  width: min(900px, calc(100% - 40px));
  padding: 120px 0 104px;
  text-align: center;
`;

const Eyebrow = styled(motion.p)`
  margin: 0 0 14px;
  color: ${({ theme }) => theme.colors.accentPale};
  font-size: clamp(0.72rem, 1.6vw, 0.92rem);
  font-weight: 800;
  letter-spacing: 0.32em;
`;

const Title = styled(motion.h1)`
  max-width: 980px;
  margin: 0;
  color: transparent;
  background: ${({ theme }) => theme.gradients.brand};
  background-clip: text;
  -webkit-background-clip: text;
  font-size: clamp(3.35rem, 9vw, 7.2rem);
  font-weight: 900;
  line-height: 0.96;
  letter-spacing: -0.065em;
  text-wrap: balance;
`;

const Motto = styled(motion.p)`
  max-width: 720px;
  margin: 28px auto 34px;
  color: rgba(255, 255, 255, 0.7);
  font-size: clamp(1.1rem, 2.6vw, 1.45rem);
  line-height: 1.5;
  letter-spacing: -0.02em;
  text-wrap: balance;
`;

const Cta = styled(motion.button)`
  min-height: 48px;
  padding: 0 22px;
  color: ${({ theme }) => theme.colors.white};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: ${({ theme }) => theme.radii.pill};
  cursor: pointer;
  font-weight: 750;
  backdrop-filter: blur(12px);
  transition: background 180ms ease, transform 180ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.17);
    transform: translateY(-2px);
  }
`;

const breatheGlow = keyframes`
  0%, 100% { opacity: 0.08; }
  50% { opacity: 1; }
`;

const Hiring = styled(Link)`
  position: relative;
  isolation: isolate;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: inherit;
    box-shadow: 0 0 8px 3px ${({ theme }) => theme.colors.white},
      0 0 26px 10px ${({ theme }) => theme.colors.accentPale},
      0 0 58px 18px ${({ theme }) => theme.colors.accent};
    pointer-events: none;
    animation: ${breatheGlow} 2.2s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    &::before { animation: none; opacity: 0.25; }
  }
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px 20px;
  width: fit-content;
  margin: 0 auto 28px;
  padding: 16px 24px;
  background: ${({ theme }) => theme.colors.accentPale};
  color: ${({ theme }) => theme.colors.bgDark};
  border-radius: ${({ theme }) => theme.radii.md};
  text-decoration: none;
  strong { font-size: 1.2rem; }
  span { font-size: .9rem; }
  &:hover { background: ${({ theme }) => theme.colors.white}; }
`;

const Hero: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const animate = reduceMotion ? undefined : { opacity: 1, y: 0 };
  const transition = { duration: 0.5, ease: 'easeOut' as const };

  const scrollToProjects = () => document.getElementById('projects')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });

  return (
    <Section>
      <SiteNav overlay />
      <Content initial={reduceMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ staggerChildren: 0.1 }}>
        <Hiring to="/careers"><strong>We are hiring.</strong><span>Explore open roles ↗</span></Hiring>
        <Eyebrow initial={reduceMotion ? false : { opacity: 0, y: 12 }} animate={animate} transition={transition}>
          GENAI TECHNOLOGIES
        </Eyebrow>
        <Title data-testid="hero-title" initial={reduceMotion ? false : { opacity: 0, y: 12 }} animate={animate} transition={{ ...transition, delay: 0.08 }}>
          AI applications, built for everyone.
        </Title>
        <Motto initial={reduceMotion ? false : { opacity: 0, y: 12 }} animate={animate} transition={{ ...transition, delay: 0.16 }}>
          Foundation models unlocked countless possibilities. We turn them into products people can use.
        </Motto>
        <Cta type="button" onClick={scrollToProjects} initial={reduceMotion ? false : { opacity: 0, y: 12 }} animate={animate} transition={{ ...transition, delay: 0.24 }}>
          View Projects ↓
        </Cta>
      </Content>
    </Section>
  );
};

export default Hero;
